/**
 * The gateway Studio resolved a charge to (`provider` on the `/api/payments`
 * response), and what guest-facing copy calls its QR.
 *
 * Studio picks the gateway from the project owner's wallet, so the booth only
 * learns it from the response. Anything this booth does not know falls back to
 * `midtrans`, which is the only safe default for older Studio responses with no
 * `provider` at all. The cost of that fallback is the QRIS logo and the word
 * "QRIS" on a code that is not QRIS, which is what an Indian booth on Razorpay
 * showed before `razorpay` was listed here.
 */
export type PaymentProvider =
  | 'midtrans'
  | 'toyyibpay'
  | 'mercadopago'
  | 'tap'
  | 'razorpay'
  // A Nayax card reader beside the booth (connected terminal only, never a QR).
  | 'nayax'

const KNOWN_PROVIDERS: ReadonlySet<string> = new Set<PaymentProvider>([
  'midtrans',
  'toyyibpay',
  'mercadopago',
  'tap',
  'razorpay',
  'nayax'
])

export function normalizePaymentProvider(value: unknown): PaymentProvider {
  const provider = typeof value === 'string' ? value.trim().toLowerCase() : ''
  return KNOWN_PROVIDERS.has(provider) ? (provider as PaymentProvider) : 'midtrans'
}

/**
 * Which `payment.rail.*` word names the QR on screen.
 *
 * - `qris`   Midtrans, outside the voucher-only regions: a real QRIS code.
 * - `upi`    Razorpay: a UPI QR, and Indian guests look for "UPI", not "QR".
 * - `online` Everything else (ToyyibPay's DuitNow, Mercado Pago, Tap).
 *
 * Kept in step with the QRIS logo gate in `pages/view/Payment.tsx`, which shows
 * the logo exactly when this answers `qris`.
 */
export type GuestRail = 'qris' | 'upi' | 'online'

export function guestRailFor(provider: PaymentProvider, isVoucherOnlyRegion: boolean): GuestRail {
  if (provider === 'razorpay') return 'upi'
  if (provider === 'midtrans' && !isVoucherOnlyRegion) return 'qris'
  return 'online'
}
